import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { EntityProvider } from './contexts/EntityContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthCallback } from './components/AuthCallback';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LandingPage } from './components/LandingPage';
import { DashboardLayout } from './components/DashboardLayout';
import { Dashboard } from './pages/Dashboard';
import { Bookkeeping } from './pages/Bookkeeping';
import { VATReturns } from './pages/VATReturns';
import { TaxInstructions } from './pages/TaxInstructions';
import { BankStatements } from './pages/BankStatements';
import { Invoices } from './pages/Invoices';
import { FinancialStatements } from './pages/FinancialStatements';
import { MeetingMinutes } from './pages/MeetingMinutes';
import { CapTable } from './pages/CapTable';
import { StockOptions } from './pages/StockOptions';
import { Settings } from './pages/Settings';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Shared layout for all protected routes - providers persist across navigation
function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <LanguageProvider>
        <EntityProvider>
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </EntityProvider>
      </LanguageProvider>
    </ProtectedRoute>
  );
}

// Router wrapper to handle auth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - must happen synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      
      {/* All protected routes share one layout (providers persist) */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/bookkeeping" element={<Bookkeeping />} />
        <Route path="/vat-returns" element={<VATReturns />} />
        <Route path="/tax-instructions" element={<TaxInstructions />} />
        <Route path="/bank-statements" element={<BankStatements />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/financial-statements" element={<FinancialStatements />} />
        <Route path="/minutes" element={<MeetingMinutes />} />
        <Route path="/cap-table" element={<CapTable />} />
        <Route path="/stock-options" element={<StockOptions />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
